import { baseApiSlice as api } from "./base-api-slice";
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    passwordAuth: build.mutation<PasswordAuthApiResponse, PasswordAuthApiArg>({
      query: (queryArg) => ({
        url: `/auth/password`,
        method: "POST",
        body: queryArg.passwordAuthRequest,
      }),
    }),
    magicAuthSend: build.mutation<
      MagicAuthSendApiResponse,
      MagicAuthSendApiArg
    >({
      query: (queryArg) => ({
        url: `/auth/magic-auth/send`,
        method: "POST",
        body: queryArg.magicAuthSendRequest,
      }),
    }),
    magicAuthVerify: build.mutation<
      MagicAuthVerifyApiResponse,
      MagicAuthVerifyApiArg
    >({
      query: (queryArg) => ({
        url: `/auth/magic-auth/verify`,
        method: "POST",
        body: queryArg.magicAuthVerifyRequest,
      }),
    }),
    ssoInitiate: build.query<SsoInitiateApiResponse, SsoInitiateApiArg>({
      query: (queryArg) => ({
        url: `/auth/sso`,
        params: {
          organization: queryArg.organization,
          returnUrl: queryArg.returnUrl,
        },
      }),
    }),
    ssoCallback: build.query<SsoCallbackApiResponse, SsoCallbackApiArg>({
      query: (queryArg) => ({
        url: `/auth/callback`,
        params: {
          code: queryArg.code,
        },
      }),
    }),
    sendPasswordReset: build.mutation<
      SendPasswordResetApiResponse,
      SendPasswordResetApiArg
    >({
      query: (queryArg) => ({
        url: `/auth/send-password-reset`,
        method: "POST",
        body: queryArg.sendPasswordResetRequest,
      }),
    }),
    getSsoConnections: build.query<
      GetSsoConnectionsApiResponse,
      GetSsoConnectionsApiArg
    >({
      query: (queryArg) => ({
        url: `/auth/sso-connections`,
        params: {
          domain: queryArg.domain,
        },
      }),
    }),
    resetPassword: build.mutation<
      ResetPasswordApiResponse,
      ResetPasswordApiArg
    >({
      query: (queryArg) => ({
        url: `/auth/reset-password`,
        method: "POST",
        body: queryArg.resetPasswordRequest,
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as workosAuthApi };
export type PasswordAuthApiResponse =
  /** status 200 Password validated successfully */ PasswordAuthResponse;
export type PasswordAuthApiArg = {
  passwordAuthRequest: PasswordAuthRequest;
};
export type MagicAuthSendApiResponse =
  /** status 200 Magic auth code sent */ MagicAuthSendResponse;
export type MagicAuthSendApiArg = {
  magicAuthSendRequest: MagicAuthSendRequest;
};
export type MagicAuthVerifyApiResponse =
  /** status 200 Authentication successful */ MagicAuthVerifyResponse;
export type MagicAuthVerifyApiArg = {
  magicAuthVerifyRequest: MagicAuthVerifyRequest;
};
export type SsoInitiateApiResponse =
  /** status 200 Authorization URL generated */ SsoInitiateResponse;
export type SsoInitiateApiArg = {
  /** Organization ID */
  organization: string;
  /** Frontend URL to redirect to after SSO authentication (supports branch deploy URLs) */
  returnUrl: string;
};
export type SsoCallbackApiResponse = unknown;
export type SsoCallbackApiArg = {
  /** OAuth authorization code from WorkOS */
  code: string;
};
export type SendPasswordResetApiResponse =
  /** status 200 Password reset email sent */ SendPasswordResetResponse;
export type SendPasswordResetApiArg = {
  sendPasswordResetRequest: SendPasswordResetRequest;
};
export type GetSsoConnectionsApiResponse =
  /** status 200 SSO connections retrieved successfully */ GetSsoConnectionsResponse;
export type GetSsoConnectionsApiArg = {
  /** The email domain to look up SSO connections for */
  domain: string;
};
export type ResetPasswordApiResponse =
  /** status 200 Password reset successful */ ResetPasswordResponse;
export type ResetPasswordApiArg = {
  resetPasswordRequest: ResetPasswordRequest;
};
export type PasswordAuthResponse = {
  /** JWT access token (returned when authentication is successful and magic auth is not required) */
  accessToken?: string | null;
  message?: string;
  redirectTo?: string;
  requiresMagicAuth?: boolean | null;
  success: boolean;
};
export type Schema = {
  /** Human-readable error message describing what went wrong. */
  error: string;
  /** Additional structured information about the error. */
  error_details?: any;
  /** High-level error category for programmatic error handling. */
  error_type:
    | "invalid_request"
    | "not_found"
    | "server_error"
    | "unauthorized"
    | "rate_limited"
    | "forbidden"
    | "closed"
    | "not_implemented";
};
export type PasswordAuthRequest = {
  email: string;
  password: string;
};
export type MagicAuthSendResponse = {
  message?: string;
  success: boolean;
};
export type MagicAuthSendRequest = {
  email: string;
};
export type MagicAuthVerifyResponse = {
  /** JWT access token (returned when verification is successful) */
  accessToken: string;
  authMethod?: "magic_auth" | "password_fallback";
  magicAuthDisabled?: boolean | null;
  message?: string;
  redirectTo?: string;
  success: boolean;
};
export type MagicAuthVerifyRequest = {
  code: string;
  email: string;
};
export type SsoInitiateResponse = {
  authorizationUrl?: string;
};
export type SendPasswordResetResponse = {
  success: boolean;
};
export type SendPasswordResetRequest = {
  email: string;
  /** The URL where users will reset their password (supports branch deploy URLs) */
  resetUrl: string;
};
export type GetSsoConnectionsResponse = {
  /** List of SSO connection types for this organization (e.g. "OktaSAML", "GoogleSAML", "AzureSAML") */
  connectionTypes: string[];
  /** The WorkOS organization ID associated with the domain */
  organizationId?: string;
};
export type ResetPasswordResponse = {
  message: string;
  success: boolean;
};
export type ResetPasswordRequest = {
  newPassword: string;
  token: string;
};
export const {
  usePasswordAuthMutation,
  useMagicAuthSendMutation,
  useMagicAuthVerifyMutation,
  useSsoInitiateQuery,
  useLazySsoInitiateQuery,
  useSsoCallbackQuery,
  useLazySsoCallbackQuery,
  useSendPasswordResetMutation,
  useGetSsoConnectionsQuery,
  useLazyGetSsoConnectionsQuery,
  useResetPasswordMutation,
} = injectedRtkApi;
